import { useState } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminTestimonials({ testimonials, onRefresh }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    text: '',
    rating: 5,
    kitten_name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await adminApi.post(`/testimonials`, testimonialForm);
      toast.success('Testimonial added successfully!');
      onRefresh();
      setIsDialogOpen(false);
      setTestimonialForm({
        name: '',
        text: '',
        rating: 5,
        kitten_name: '',
      });
    } catch {
      toast.error('Failed to add testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await adminApi.delete(`/testimonials/${id}`);
      toast.success('Testimonial deleted successfully!');
      onRefresh();
    } catch {
      toast.error('Failed to delete testimonial');
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-stone-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-medium text-stone-800">Testimonials</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-testimonial-button"
              className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Testimonial
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Testimonial</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Customer Name *
                </label>
                <Input
                  value={testimonialForm.name}
                  onChange={(e) =>
                    setTestimonialForm({ ...testimonialForm, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Testimonial *
                </label>
                <Textarea
                  value={testimonialForm.text}
                  onChange={(e) =>
                    setTestimonialForm({ ...testimonialForm, text: e.target.value })
                  }
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">Rating</label>
                <Select
                  value={String(testimonialForm.rating)}
                  onValueChange={(value) =>
                    setTestimonialForm({ ...testimonialForm, rating: parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Kitten Name (Optional)
                </label>
                <Input
                  value={testimonialForm.kitten_name}
                  onChange={(e) =>
                    setTestimonialForm({ ...testimonialForm, kitten_name: e.target.value })
                  }
                />
              </div>
              <Button
                type="submit"
                data-testid="save-testimonial-button"
                className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
              >
                Add Testimonial
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testimonials.map((testimonial) => (
          <div
            key={testimonial.id}
            data-testid={`testimonial-${testimonial.id}`}
            className="bg-stone-50 p-6 rounded-xl border border-stone-100"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={`${testimonial.id}-star-${i}`} className="text-yellow-500">
                    ⭐
                  </span>
                ))}
              </div>
              <Button
                onClick={() => handleDelete(testimonial.id)}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-testimonial-${testimonial.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-stone-600 mb-3">{testimonial.text}</p>
            <p className="font-semibold text-stone-800">{testimonial.name}</p>
            {testimonial.kitten_name && (
              <p className="text-sm text-stone-500">Kitten: {testimonial.kitten_name}</p>
            )}
          </div>
        ))}
      </div>
      {testimonials.length === 0 && (
        <div className="text-center py-12 text-stone-500">
          No testimonials yet. Click "Add Testimonial" to get started.
        </div>
      )}
    </div>
  );
}
